/**
 * Permission codenames — must stay in sync with
 * approveit-backend/rbac/permissions.py (single source of truth).
 *
 * UI code SHOULD reference Perm.WORKFLOW_DELETE (etc), not the raw
 * string, so that renames cascade through TypeScript.
 *
 * Roles only answer "what actions can this user perform?" — they do
 * not gate access to specific workflows. Workflow visibility is
 * controlled separately by workflow ACL (Privacy Settings on each
 * workflow); the two systems compose at the request boundary.
 *
 * Frontend permission checks are UX hints only — every authorization
 * decision is re-evaluated on the backend via rbac.services.has_perm.
 */

export const Perm = {
  // Workflow
  WORKFLOW_VIEW: 'workflow.view',
  // Admin override — read all workflow configs across departments
  // (does NOT extend to AR content). Intended for IT Admin/Super Admin.
  WORKFLOW_VIEW_ALL: 'workflow.view_all',
  WORKFLOW_ADD: 'workflow.add',
  WORKFLOW_CHANGE: 'workflow.change',
  WORKFLOW_DELETE: 'workflow.delete',

  // Pipeline

  // Approval Request — minimal model: view + create + admin override.
  // Per-action codenames (REQUEST_CHANGE / REQUEST_DELETE /
  // REQUEST_APPROVE / etc.) were dropped pre-launch in favour of the
  // natural per-row ACL (requestor/approver). REQUEST_MANAGE is the
  // admin override for stepping in on someone else's request.
  REQUEST_VIEW: 'request.view',
  // ACL-respecting "view all": see every request except those in
  // ACL-locked workflows the holder isn't granted. Sits between
  // REQUEST_VIEW (participant-only) and REQUEST_VIEW_ALL (full bypass).
  // Backend-enforced on the AR list/detail; kept here to mirror the
  // catalog. No FE gate reads it today.
  REQUEST_VIEW_ALL_SCOPED: 'request.view_all_scoped',
  REQUEST_VIEW_ALL: 'request.view_all',
  REQUEST_ADD: 'request.add',
  REQUEST_MANAGE: 'request.manage',

  ACCOUNTING_MANAGE: 'accounting.manage',
  ACCOUNTING_MANAGE_SETTINGS: 'accounting.manage_settings',

  // Feature-level view perms — one per sidebar tab. Replaced the
  // legacy ui_features workspace toggle matrix.
  BILL_VIEW: 'bill.view',
  PURCHASE_ORDER_VIEW: 'purchase_order.view',
  RFQ_VIEW: 'rfq.view',
  VENDOR_VIEW: 'vendor.view',
  INVOICE_VIEW: 'invoice.view',
  QUOTE_VIEW: 'quote.view',
  CUSTOMER_VIEW: 'customer.view',
  COST_CENTER_VIEW: 'cost_center.view',
  PRODUCT_VIEW: 'product.view',
  CHART_OF_ACCOUNTS_VIEW: 'chart_of_accounts.view',
  CATEGORY_VIEW: 'category.view',
  TAX_VIEW: 'tax.view',
  // Tasks — minimal model. Edit/delete are governed by per-row ACL
  // (creators and assignees can act on their own tasks). TASK_MANAGE
  // is the admin override.
  TASK_VIEW: 'task.view',
  TASK_ADD: 'task.add',
  TASK_MANAGE: 'task.manage',
  MAILBOX_VIEW: 'mailbox.view',

  // Contracts (Legal Module — separate from accounting)
  CONTRACT_VIEW: 'contract.view',
  CONTRACT_MANAGE: 'contract.manage',
  CONTRACT_MANAGE_CATEGORIES: 'contract.manage_categories',
  CONTRACT_MANAGE_CATEGORY_ACCESS: 'contract.manage_category_access',

  // Team & Organization
  TEAM_VIEW: 'team.view',
  CUSTOM_LIST_VIEW: 'custom_list.view',
  CUSTOM_LIST_ADD: 'custom_list.add',
  CUSTOM_LIST_CHANGE: 'custom_list.change',
  CUSTOM_LIST_DELETE: 'custom_list.delete',
  CUSTOM_LIST_MANAGE_ALL: 'custom_list.manage_all',
  TEAM_MANAGE_ORG_STRUCTURE: 'team.manage_org_structure',
  // Split from the legacy team.manage_members bundle. New code should
  // use these narrower codenames; the legacy codename is alias-resolved
  // on the backend so existing roles keep working.
  TEAM_MANAGE_MEMBER_PROFILES: 'team.manage_member_profiles',
  TEAM_MANAGE_MEMBER_ROLES: 'team.manage_member_roles',
  TEAM_INVITE_MEMBER: 'team.invite_member',
  TEAM_MANAGE_SETTINGS: 'team.manage_settings',

  // Integrations
  INTEGRATION_VIEW: 'integration.view',
  INTEGRATION_MANAGE: 'integration.manage',

  // Billing
  BILLING_VIEW: 'billing.view',
  BILLING_MANAGE: 'billing.manage',

  // Report
  REPORT_VIEW: 'report.view',

  // AI Assistant. No call site reads it: the backend composes it with the
  // `ai_assistant` Features row and ships the result in `features`, which
  // is what the nav and the route guards read. Listed because this map
  // mirrors the backend catalog.
  AI_ASSISTANT_USE: 'ai_assistant.use',

  // AI connections (the MCP server). Same shape as the line above: no
  // call site reads it, because the backend composes it with the
  // `ai_connections` Features row and the plan and ships the result —
  // per workspace — in the `workspaces` list on GET /mcp/keys. Listed
  // because this map mirrors the backend catalog.
  AI_CONNECTIONS_USE: 'ai_connections.use',

  // Audit Log
  AUDIT_LOG_VIEW: 'audit_log.view',

  // Roles & permissions
  ROLE_VIEW: 'role.view',
  ROLE_MANAGE: 'role.manage',
} as const

export type PermCodename = (typeof Perm)[keyof typeof Perm]

// Perms that each grant access to at least one Team Settings tab. The
// /team-settings route and the account-menu entry admit a user holding
// ANY of these, so a granular role (e.g. Integrations-only or Audit-only)
// reaches its tab instead of being bounced by a blanket
// team.manage_settings gate.
// Keep in sync with TeamSettingsTabsComponents in pages/TeamSettings/constants.
// (Defined here, not derived from that table, to keep this module free of
// the heavy page-component imports the table pulls in.)
export const TEAM_SETTINGS_PERMS: PermCodename[] = [
  Perm.TEAM_MANAGE_SETTINGS,
  // INTEGRATION_VIEW (read-only) is enough to reach the Integrations tab;
  // the tab renders read-only and gates its mutating actions on
  // INTEGRATION_MANAGE. INTEGRATION_MANAGE implies INTEGRATION_VIEW on the
  // backend, so manage-holders satisfy this too.
  Perm.INTEGRATION_VIEW,
  Perm.AUDIT_LOG_VIEW,
]

export interface RoleInfo {
  id: number
  name: string
  is_system: boolean
}
